<template>
  <div class="admin-auth-root min-vh-100 d-flex flex-column justify-content-between p-3 p-md-4">
    <!-- Top Bar -->
    <div class="d-flex justify-content-between align-items-center mb-2 px-1">
      <div class="d-flex align-items-center gap-2">
        <span class="system-status-pill">
          <span class="status-dot"></span>
          <span>{{ $currentLocale === 'id' ? 'PORTAL KOMANDO GAMITEAM' : 'GAMITEAM COMMAND PORTAL' }}</span>
        </span>
      </div>
      <div class="d-flex align-items-center gap-2">
        <!-- Language Switcher -->
        <div class="lang-switch-group">
          <button
            type="button"
            class="lang-btn"
            :class="{ active: $currentLocale === 'en' }"
            @click="$setLocale('en')"
          >
            EN
          </button>
          <span class="lang-divider">/</span>
          <button
            type="button"
            class="lang-btn"
            :class="{ active: $currentLocale === 'id' }"
            @click="$setLocale('id')"
          >
            ID
          </button>
        </div>

        <a href="http://localhost:3000" class="player-link">
          <i class="mdi mdi-shield-sword-outline me-1" aria-hidden="true"></i>{{ $t('auth.playerLink') }} <span class="arrow" aria-hidden="true">↗</span>
        </a>
      </div>
    </div>

    <!-- Centered Login Card -->
    <div class="d-flex align-items-center justify-content-center flex-grow-1 py-3">
      <div class="login-box w-100">
        <div class="login-panel">
          <!-- Header -->
          <div class="text-center mb-4">
            <div class="d-inline-flex align-items-center justify-content-center gap-2 mb-2">
              <div class="kh-pennant">
                <i class="mdi mdi-shield-crown-outline" style="color: #fbbf24; font-size: 22px;" aria-hidden="true"></i>
              </div>
            </div>
            <h1 class="panel-heading text-white" style="font-family: 'Russo One', sans-serif; letter-spacing: 0.04em;">
              {{ $t('auth.loginTitle') }}
            </h1>
            <p class="panel-subheading">
              {{ $t('auth.loginSubtitle') }}
            </p>
          </div>

          <!-- Role Quick Switcher -->
          <div class="role-selector-bar mb-3">
            <div class="selector-meta d-flex justify-content-between align-items-center mb-2 px-1">
              <span class="selector-label">{{ $t('auth.chooseCommandClass') }}</span>
              <span class="selector-note">{{ $t('auth.demoAccess') }}</span>
            </div>
            <div class="role-segmented-controls">
              <button
                type="button"
                class="role-segment-btn"
                :class="{ active: credentials.email === 'superadmin@mailinator.com' }"
                @click="fillProfile('superadmin@mailinator.com', 'admin123')"
              >
                <span class="role-title">{{ $t('auth.sovereignRole') }}</span>
                <span class="role-sub">{{ $t('auth.sovereignSub') }}</span>
              </button>
              <button
                type="button"
                class="role-segment-btn"
                :class="{ active: credentials.email === 'owner@mailinator.com' }"
                @click="fillProfile('owner@mailinator.com', 'admin123')"
              >
                <span class="role-title">{{ $t('auth.ownerRole') }}</span>
                <span class="role-sub">{{ $t('auth.ownerSub') }}</span>
              </button>
              <button
                type="button"
                class="role-segment-btn"
                :class="{ active: credentials.email === 'maker@mailinator.com' }"
                @click="fillProfile('maker@mailinator.com', 'admin123')"
              >
                <span class="role-title">{{ $t('auth.makerRole') }}</span>
                <span class="role-sub">{{ $t('auth.makerSub') }}</span>
              </button>
            </div>
          </div>

          <!-- Login Form -->
          <form @submit.prevent="handleLogin" autocomplete="off">
            <!-- Email -->
            <div class="form-group mb-3">
              <label class="form-label" for="loginEmailInput">
                {{ $t('auth.emailLabel') }}
              </label>
              <input
                id="loginEmailInput"
                type="text"
                class="form-control-input"
                v-model="credentials.email"
                :placeholder="$t('auth.emailPlaceholder')"
                required
                autocapitalize="none"
                autocorrect="off"
                spellcheck="false"
                autocomplete="username"
              />
            </div>

            <!-- Password -->
            <div class="form-group mb-4">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="form-label mb-0" for="loginPasswordInput">
                  {{ $t('auth.passwordLabel') }}
                </label>
                <a
                  href="javascript:void(0)"
                  class="forgot-link"
                  @click="isModalOpen = true"
                >
                  {{ $t('auth.forgotPassword') }}
                </a>
              </div>
              <div class="password-input-wrapper">
                <input
                  id="loginPasswordInput"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-control-input password-field"
                  v-model="credentials.password"
                  :placeholder="$t('auth.passwordPlaceholder')"
                  required
                  autocapitalize="none"
                  autocorrect="off"
                  spellcheck="false"
                  autocomplete="current-password"
                />
                <button
                  type="button"
                  class="password-toggle-btn"
                  @click="showPassword = !showPassword"
                  :aria-label="showPassword ? ($currentLocale === 'id' ? 'Sembunyikan kata sandi' : 'Hide password') : ($currentLocale === 'id' ? 'Tampilkan kata sandi' : 'Show password')"
                  :title="showPassword ? ($currentLocale === 'id' ? 'Sembunyikan kata sandi' : 'Hide password') : ($currentLocale === 'id' ? 'Tampilkan kata sandi' : 'Show password')"
                >
                  <svg v-if="!showPassword" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <svg v-else aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              class="primary-submit-btn"
              :disabled="loading"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              <span>{{ loading ? $t('auth.signingIn') : $t('auth.signInBtn') }}</span>
            </button>

            <!-- Self-Service Registration / Onboarding Link -->
            <div class="mt-4 pt-3 text-center" style="border-top: 1px solid #182949;">
              <p class="small mb-2" style="color: #94a3b8;">
                {{ $currentLocale === 'en' ? 'Interested in Gamiteam for your organization?' : 'Perusahaan Anda tertarik menggunakan Gamiteam?' }}
              </p>
              <router-link
                :to="{ name: 'Register' }"
                class="btn btn-outline-warning w-100 py-2 d-flex align-items-center justify-content-center gap-2 font-weight-bold"
              >
                <i class="mdi mdi-shield-crown-outline" aria-hidden="true"></i>
                <span>{{ $currentLocale === 'en' ? 'Register Workspace (14-Day Free Trial)' : 'Daftarkan Perusahaan (Uji Coba Gratis 14 Hari)' }}</span>
              </router-link>
            </div>
          </form>

          <!-- Security Badge -->
          <div class="security-badge-footer">
            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
            <span>{{ $t('auth.citadelGuard') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Page Footer -->
    <div class="admin-page-footer text-center">
      <span>&copy; {{ new Date().getFullYear() }} {{ $t('auth.citadelEdition') }}</span>
    </div>

    <!-- Forgot Password Modal -->
    <div
      v-if="isModalOpen"
      class="custom-modal-backdrop d-flex align-items-center justify-content-center"
      style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(3,10,26,0.84); z-index: 1050; backdrop-filter: blur(8px);"
    >
      <div class="modal-card-panel" style="width: 420px; max-width: 90%;">
        <div class="modal-card-header d-flex justify-content-between align-items-center p-3 border-bottom border-subtle">
          <h5 class="modal-heading mb-0 kh-title">
            {{ $t('auth.resetPasswordTitle') }}
          </h5>
          <button
            type="button"
            class="modal-close-btn"
            :aria-label="$t('common.cancel')"
            @click="isModalOpen = false"
          >
            ✕
          </button>
        </div>
        <div class="p-4">
          <p class="small mb-3" style="color: #bae6fd;">
            {{ $t('auth.resetPasswordDesc') }}
          </p>
          <div class="form-group mb-0">
            <label class="form-label" for="forgotEmailInput">
              {{ $t('auth.emailLabel') }}
            </label>
            <input
              id="forgotEmailInput"
              type="email"
              v-model="forgotEmail"
              class="form-control-input"
              :placeholder="$t('auth.emailPlaceholder')"
              required
            />
          </div>
        </div>
        <div class="p-3 border-top border-subtle d-flex justify-content-end gap-2">
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            @click="isModalOpen = false"
          >
            {{ $t('common.cancel') }}
          </button>
          <button
            type="button"
            class="btn btn-warning btn-sm"
            @click="processForgotPassword"
            :disabled="!forgotEmail || forgotLoading"
          >
            <span v-if="forgotLoading" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('auth.sendResetLink') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { login, forgotPasswordAPI, revokeTokenSilently } from "@/services/authService";
import { parseRoles, hasAdminPortalAccess, isPurePlayer } from "@/utils/roles";
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "LoginView",
  data: () => ({
    credentials: {
      email: "",
      password: "",
    },
    showPassword: false,
    forgotEmail: "",
    isModalOpen: false,
    loading: false,
    forgotLoading: false,
  }),
  methods: {
    fillProfile(email, password) {
      this.credentials.email = email;
      this.credentials.password = password;
    },
    async handleLogin() {
      this.loading = true;
      try {
        const response = await login(this.credentials);
        const userData = response?.data || {};
        const token = userData.token;
        const tokenType = userData.token_type || 'Bearer';

        if (!hasAdminPortalAccess(userData.role)) {
          await revokeTokenSilently(token);
          const isId = this.$currentLocale === 'id';
          Swal.fire({
            icon: "error",
            title: isId ? "Akses Ditolak" : "Access Denied",
            text: isPurePlayer(userData.role)
              ? (isId
                  ? "Pemain harus menggunakan Aplikasi Player. Akses ke Portal Admin dibatasi."
                  : "Players must use the Player App. Access to the Admin Portal is restricted.")
              : (isId
                  ? "Anda tidak memiliki izin untuk mengakses Portal Admin."
                  : "You do not have permission to access the Admin Portal."),
            confirmButtonColor: "#f59e0b",
          });
          return;
        }

        axios.defaults.headers.common['Authorization'] = `${tokenType} ${token}`;

        this.$store.dispatch("updateAuthToken", token);
        this.$store.dispatch("updateTokenType", tokenType);
        this.$store.dispatch("updateUser", userData);

        const roles = parseRoles(userData.role);
        let targetPath = "/dashboard";
        if (roles.includes('superadmin')) {
          targetPath = "/dashboard";
        } else if (roles.includes('owner')) {
          targetPath = "/owner/analytics";
        } else if (roles.includes('maker')) {
          targetPath = "/maker/studio";
        }
        this.$router.push(targetPath).catch(() => {});
      } catch (error) {
        const errors = error.response?.data?.errors;
        const msg =
          (errors && (errors.password?.[0] || errors.email?.[0])) ||
          error.response?.data?.message ||
          (this.$currentLocale === 'id'
            ? 'Autentikasi gagal. Silakan periksa kredensial Anda.'
            : 'Authentication failed. Please verify your credentials.');
        Swal.fire({
          icon: "error",
          title: this.$t('auth.signInFailed') || (this.$currentLocale === 'id' ? 'Gagal Masuk' : 'Sign In Failed'),
          text: msg,
          confirmButtonColor: "#f59e0b",
        });
      } finally {
        this.loading = false;
      }
    },
    async processForgotPassword() {
      if (!this.forgotEmail) return;
      this.forgotLoading = true;
      try {
        const response = await forgotPasswordAPI(this.forgotEmail);
        this.isModalOpen = false;
        Swal.fire({
          icon: "success",
          title: this.$t('auth.checkInbox') || (this.$currentLocale === 'id' ? 'Periksa Kotak Masuk Anda' : 'Check Your Inbox'),
          text: response.message || (this.$currentLocale === 'id'
            ? `Instruksi pemulihan kata sandi telah dikirim ke ${this.forgotEmail}.`
            : `Password reset instructions sent to ${this.forgotEmail}.`),
          confirmButtonColor: "#f59e0b",
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: this.$t('auth.requestFailed') || (this.$currentLocale === 'id' ? 'Permintaan Gagal' : 'Request Failed'),
          text: err.response?.data?.message || (this.$currentLocale === 'id'
            ? 'Akun dengan email ini tidak ditemukan.'
            : 'No account found with this email address.'),
          confirmButtonColor: "#f59e0b",
        });
      } finally {
        this.forgotLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-auth-root {
  background: #060d1a;
  color: #f8fafc;
}

.system-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #0c182e;
  border: 1px solid #1e3358;
  padding: 5px 12px;
  border-radius: 8px;
  font-family: 'Russo One', sans-serif;
  font-size: 10px;
  letter-spacing: 0.04em;
  color: #cbd5e1;
}

.status-dot {
  width: 7px;
  height: 7px;
  background-color: #fbbf24;
  border-radius: 50%;
}

.lang-switch-group {
  display: inline-flex;
  align-items: center;
  background: #0c182e;
  border: 1px solid #1e3358;
  border-radius: 8px;
  padding: 2px 6px;
}

.lang-btn {
  background: transparent;
  border: none;
  color: #94a3b8;
  font-family: 'Russo One', sans-serif;
  font-size: 10px;
  padding: 3px 7px;
  cursor: pointer;
  border-radius: 5px;
}

.lang-btn.active {
  color: #1a0f00;
  background: #fbbf24;
}

.lang-divider {
  font-size: 10px;
  color: #1e3358;
}

.player-link {
  color: #fbbf24;
  font-family: 'Russo One', sans-serif;
  font-size: 11px;
  text-decoration: none;
  padding: 5px 12px;
  border-radius: 8px;
  border: 1px solid rgba(251, 191, 36, 0.35);
  background: rgba(251, 191, 36, 0.1);
}

.player-link:hover {
  color: #ffffff;
  background: rgba(251, 191, 36, 0.2);
}

.login-box {
  max-width: 440px;
}

.login-panel {
  background: #0c182e;
  border: 1px solid #1e3358;
  border-radius: 14px;
  padding: 32px 28px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.55);
}

.panel-heading {
  font-size: 20px;
  margin-bottom: 4px;
}

.panel-subheading {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 600;
  margin-bottom: 0;
}

.role-selector-bar {
  background: #081121;
  border: 1px solid #182949;
  border-radius: 10px;
  padding: 12px;
}

.selector-label {
  font-family: 'Russo One', sans-serif;
  font-size: 10px;
  letter-spacing: 0.06em;
  color: #fbbf24;
}

.selector-note {
  font-family: 'Chakra Petch', sans-serif;
  font-size: 9px;
  font-weight: 700;
  color: #94a3b8;
}

.role-segmented-controls {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.role-segment-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 6px;
  background: #0c182e;
  border: 1px solid #1e3358;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  color: #cbd5e1;
}

.role-segment-btn:hover {
  border-color: rgba(251, 191, 36, 0.45);
  color: #ffffff;
}

.role-segment-btn.active {
  background: rgba(251, 191, 36, 0.14);
  border-color: #fbbf24;
  color: #fbbf24;
}

.role-title {
  font-family: 'Russo One', sans-serif;
  font-size: 10px;
}

.role-sub {
  font-size: 9px;
  color: #94a3b8;
  font-weight: 600;
}

.form-label {
  display: block;
  font-family: 'Chakra Petch', sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #cbd5e1;
  margin-bottom: 6px;
}

.form-control-input {
  width: 100%;
  background: #081121;
  border: 1px solid #1e3358;
  border-radius: 8px;
  padding: 10px 12px;
  color: #ffffff;
  font-weight: 600;
  font-size: 13px;
}

.form-control-input:focus-visible,
.lang-btn:focus-visible,
.role-segment-btn:focus-visible,
.password-toggle-btn:focus-visible,
.primary-submit-btn:focus-visible,
.player-link:focus-visible {
  outline: 2px solid #fbbf24;
  outline-offset: 2px;
}

.form-control-input:focus {
  border-color: #fbbf24;
  box-shadow: 0 0 0 2px rgba(251, 191, 36, 0.18);
}

.password-input-wrapper {
  position: relative;
}

.password-field {
  padding-right: 44px;
}

.password-toggle-btn {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  padding: 0;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.forgot-link {
  font-size: 11px;
  font-weight: 700;
  color: #fbbf24;
  text-decoration: none;
}

.primary-submit-btn {
  width: 100%;
  background: linear-gradient(180deg, #fcd34d 0%, #f59e0b 100%);
  color: #1a0f00;
  border: 1px solid #fde68a;
  border-radius: 9px;
  padding: 11px 16px;
  font-family: 'Russo One', sans-serif;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  box-shadow: 0 2px 0 #92400e;
  display: flex;
  align-items: center;
  justify-content: center;
}

.primary-submit-btn:hover:not(:disabled) {
  filter: brightness(1.05);
}

.security-badge-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #182949;
  font-family: 'Chakra Petch', sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
}

.admin-page-footer {
  font-family: 'Chakra Petch', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
}

.modal-card-panel {
  background: #0c182e;
  border: 1px solid #1e3358;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.75);
}

.modal-close-btn {
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 14px;
  cursor: pointer;
}

.border-subtle {
  border-color: #182949 !important;
}
</style>
